// Guards against "user" text-changes that were not made by the user.
//
// Quill reports every DOM mutation inside the editor as a `user` change, whether it
// came from the keyboard or from a browser feature / extension (page translation,
// spellcheck, grammar tools, ...) rewriting the editor's text nodes. RichTextField
// forwards every `user` change to ShareDB, so such a mutation wipes the answer for
// every collaborator at once. We therefore only trust destructive deltas when a real
// input event was observed on the editor shortly before they were emitted.

export const INTENT_WINDOW_MS = 1500;
export const DESTRUCTIVE_DELETE_CHARS = 20;

const opsOf = (delta) => {
  if (Array.isArray(delta)) return delta;
  return (delta && delta.ops) || [];
};

export const countDeleted = (delta) =>
  opsOf(delta).reduce((total, op) => total + (op.delete || 0), 0);

const countInserted = (delta) =>
  opsOf(delta).reduce((total, op) => {
    if (typeof op.insert === 'string') return total + op.insert.length;
    // Embeds (formulas, images, ...) occupy a single character in Quill.
    if (op.insert !== undefined) return total + 1;
    return total;
  }, 0);

export const hasRecentIntent = (
  lastIntentAt,
  now,
  windowMs = INTENT_WINDOW_MS
) => lastIntentAt != null && now - lastIntentAt < windowMs;

/**
 * Decide whether a `user`-sourced Quill delta should be dropped instead of being
 * submitted to ShareDB.
 *
 * delta:         Quill Delta (or its `ops` array) reported by the text-change event
 * lengthAfter:   quill.getLength() after the change was applied to the editor
 * lastIntentAt:  timestamp of the last real input event seen on the editor, or null
 * now:           current timestamp (injectable for tests)
 */
export function shouldBlockUserDelta({
  delta,
  lengthAfter,
  lastIntentAt,
  now = Date.now(),
  intentWindowMs = INTENT_WINDOW_MS,
  destructiveDeleteChars = DESTRUCTIVE_DELETE_CHARS,
}) {
  const deleted = countDeleted(delta);
  if (deleted === 0) return false;
  if (hasRecentIntent(lastIntentAt, now, intentWindowMs)) return false;

  // Quill always keeps a trailing newline that cannot be deleted, so "the whole
  // field" is everything but that last character.
  const lengthBefore = lengthAfter - countInserted(delta) + deleted;
  const wipesField = deleted >= lengthBefore - 1;

  return deleted >= destructiveDeleteChars || wipesField;
}

// Events that only the user (or the user agent on their behalf) can produce on the
// editor. Script-driven DOM rewrites by extensions or browser features never fire
// these, which is what lets us tell the two apart.
export const ROOT_INTENT_EVENTS = [
  'beforeinput',
  'input',
  'keydown',
  'paste',
  'cut',
  'drop',
  'compositionstart',
  'compositionupdate',
  'compositionend',
];

// Toolbar buttons change content through Quill's API without any input event on
// the editor root, so a press on the toolbar counts as intent too.
export const TOOLBAR_INTENT_EVENTS = ['mousedown'];

/**
 * Register `onIntent` for every real-input event on the editor root (and toolbar,
 * when given). Listeners run in the capture phase so intent is recorded before
 * Quill turns the resulting DOM mutation into a text-change. Returns a cleanup.
 */
export function attachIntentTracking({ root, toolbar, onIntent }) {
  const subscriptions = [];
  const listen = (target, events) => {
    if (!target) return;
    events.forEach((type) => {
      target.addEventListener(type, onIntent, true);
      subscriptions.push([target, type]);
    });
  };

  listen(root, ROOT_INTENT_EVENTS);
  listen(toolbar, TOOLBAR_INTENT_EVENTS);

  return () => {
    subscriptions.forEach(([target, type]) =>
      target.removeEventListener(type, onIntent, true)
    );
  };
}

/**
 * Quill ignores the last newline of a delta, which breaks rendering and caption
 * capture, so the editor is loaded with an extra trailing newline. This returns a
 * new delta instead of mutating the ShareDB snapshot in place.
 */
export function withTrailingNewline(data) {
  if (!data || !data.ops) return data;
  return { ...data, ops: [...data.ops, { insert: '\n' }] };
}
